import { createContext, JSX, useState } from "react";

import { ProjectService } from "@app/projects";
import { InvalidItemsTableData, ResultsSummaryTableData, ReviewProjectsTableData, BulkProviderChangeProjectTableData } from "../../shared/provider-change-wizards/interfaces";
import { BulkTargetingWizardSteps , BulkTargetExistingTargetActionEnum } from "./interfaces";
import ProjectionProviderService from "../../projects/projection-provider/projectionProviderService";
import { reportName } from "../../exports/export-panel-body/exportPanelBodyHelpers";

/* eslint-disable sort-keys */
interface BulkTargetingWizardContextProps {
  projectService: ProjectService;
  projectionProviderService: ProjectionProviderService;

  bulkProviderTargetId: number;
  bulkImportFormData: FormData;
  comment: string;
  emailOnSuccess: boolean;
  filename: string;
  formatType: string;
  invalidItems: InvalidItemsTableData[];
  projects: BulkProviderChangeProjectTableData[];
  resultsSummary: ResultsSummaryTableData[];
  reviewProjects: ReviewProjectsTableData[];
  page: BulkTargetingWizardSteps;
  selectedProjectIds: string[];
  targetingReason: string;
  existingTargetAction: BulkTargetExistingTargetActionEnum;
  uploadedTargetCount: number;
  isDistinctServicingProviderReport: boolean;

  setBulkProviderTargetId: (id: number) => void;
  setBulkImportFormData: (formData: FormData) => void;
  setComment: (newComment: string) => void;
  setEmailOnSuccess: (flag: boolean) => void;
  setFormatType: (newType: string) => void;
  setFilename: (newFilename: string) => void;
  setInvalidItems: (invalidItems: InvalidItemsTableData[]) => void;
  setPage: (newPage: BulkTargetingWizardSteps) => void;
  setProjects: (projects: BulkProviderChangeProjectTableData[]) => void;
  setResultsSummary: (results: ResultsSummaryTableData[]) => void;
  setReviewProjects: (projects: ReviewProjectsTableData[]) => void;
  setSelectedProjectIds: (ids: string[]) => void;
  setTargetingReason: (targetingReason: string) => void;
  setExistingTargetAction: (action: BulkTargetExistingTargetActionEnum) => void;
  setUploadedTargetCount: (count: number) => void;
  setIsDistinctServicingProviderReport: (isDistinct: boolean) => void;
}

const BulkTargetingWizardContext = createContext<Partial<BulkTargetingWizardContextProps>>({
  comment: "",
  emailOnSuccess: true,
  filename: "",
  formatType: "CSV",
  invalidItems: [],
  page: BulkTargetingWizardSteps.SelectProjects,
  projects: [],
  resultsSummary: [],
  reviewProjects: [],
  selectedProjectIds: [],
  targetingReason: "",
  uploadedTargetCount: 0,
  isDistinctServicingProviderReport: false
});

const BulkTargetingWizardProvider = (props: Partial<BulkTargetingWizardContextProps> & { children: React.ReactNode }): JSX.Element => {
  const projectService = new ProjectService();
  const projectionProviderService = new ProjectionProviderService();

  const [bulkImportFormData, setBulkImportFormData] = useState<FormData>();
  const [comment, setComment] = useState("");
  const [emailOnSuccess, setEmailOnSuccess] = useState(true);
  const [filename, setFilename] = useState(reportName("VolumeTargeting"));
  const [formatType, setFormatType] = useState("CSV");
  const [invalidItems, setInvalidItems] = useState<InvalidItemsTableData[]>([]);
  const [projects, setProjects] = useState<BulkProviderChangeProjectTableData[]>([]);
  const [resultsSummary, setResultsSummary] = useState<ResultsSummaryTableData[]>([]);
  const [reviewProjects, setReviewProjects] = useState<ReviewProjectsTableData[]>([]);
  const [page, setPage] = useState<BulkTargetingWizardSteps>(BulkTargetingWizardSteps.SelectProjects);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [bulkProviderTargetId, setBulkProviderTargetId] = useState<number>();
  const [targetingReason, setTargetingReason] = useState<string>("");
  const [existingTargetAction, setExistingTargetAction] = useState<BulkTargetExistingTargetActionEnum>();
  const [uploadedTargetCount, setUploadedTargetCount] = useState<number>(null);
  const [isDistinctServicingProviderReport, setIsDistinctServicingProviderReport] = useState<boolean>(false);


  const value = {
    projectService,
    projectionProviderService,

    bulkProviderTargetId,
    bulkImportFormData,
    comment,
    emailOnSuccess,
    filename,
    formatType,
    invalidItems,
    isDistinctServicingProviderReport,
    projects,
    resultsSummary,
    reviewProjects,
    page,
    selectedProjectIds,
    targetingReason,
    existingTargetAction,
    uploadedTargetCount,

    setBulkProviderTargetId,
    setBulkImportFormData,
    setComment,
    setEmailOnSuccess,
    setFormatType,
    setFilename,
    setInvalidItems,
    setIsDistinctServicingProviderReport,
    setPage,
    setProjects,
    setResultsSummary,
    setReviewProjects,
    setSelectedProjectIds,
    setTargetingReason,
    setExistingTargetAction,
    setUploadedTargetCount
  };

  return (
    <BulkTargetingWizardContext.Provider value={value}>
      {props.children}
    </BulkTargetingWizardContext.Provider>
  );

};

export { BulkTargetingWizardContext, BulkTargetingWizardProvider };
